// PR-26: fullscreen card detail.
// PR-33: edit support + jump-from-wikilink.
//
// Read by default; tap "Edit" → switch to an editable form (kind picker,
// title, body, pack). Save fires api.patchCard via the parent; Cancel
// reverts. Esc closes; Capacitor will hook the hardware back gesture in
// a later PR.
//
// We deliberately don't port the desktop CardPeek's history drawer or
// implementation-status pill in this PR — those are heavy ops still
// preferred on desktop. PR-35 can lift them in if needed.

import { useEffect, useState } from 'react'
import type { Card, CardPatch } from '../../api'
import { CardBodyMarkdown } from '../cards/CardBodyMarkdown'
import { KindPill } from './MobileCards'
import { KIND_SPECS } from '../../kinds'

interface MobileCardPeekProps {
  card: Card
  /** All cards in the active project. Used to resolve `[[c-X]]` wikilinks
   *  in the rendered body to clickable jumps. Empty Set is acceptable. */
  knownIds: ReadonlySet<string>
  onClose: () => void
  /** Save the patched card. Parent handles network + local state update;
   *  resolves once the new card is reflected in `cards`. Reject to bubble
   *  up an error which the peek surfaces inline. */
  onSave: (patch: CardPatch) => Promise<void>
  /** Cross-tab jump: tapping a wikilink in this card's body re-anchors
   *  the peek to the target card. Wired in MobileShell. */
  onJumpToCard: (cardId: string) => void
}

export function MobileCardPeek({
  card,
  knownIds,
  onClose,
  onSave,
  onJumpToCard,
}: MobileCardPeekProps) {
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const [draft, setDraft] = useState<CardPatch>({})
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // Reset to view mode when the peeked card changes (e.g. user clicked a
  // wikilink jumping to a different card while editing this one).
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMode('view')
    setDraft({})
    setErr(null)
  }, [card.card_id])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        if (mode === 'edit') {
          setMode('view')
          setDraft({})
        } else {
          onClose()
        }
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose, mode])

  const draftKind = draft.kind ?? card.kind
  const draftTitle = draft.title ?? card.title ?? ''
  const draftBody = draft.body ?? card.body
  const draftPack = draft.pack ?? card.pack ?? ''

  const dirty =
    draftKind !== card.kind ||
    draftTitle !== (card.title ?? '') ||
    draftBody !== card.body ||
    draftPack !== (card.pack ?? '')

  const commit = async () => {
    if (!dirty || saving) return
    setSaving(true)
    setErr(null)
    try {
      // Only send fields that actually changed — the server treats
      // omitted fields as "leave alone".
      const patch: CardPatch = {}
      if (draftKind !== card.kind) patch.kind = draftKind
      if (draftTitle !== (card.title ?? '')) patch.title = draftTitle
      if (draftBody !== card.body) patch.body = draftBody
      if (draftPack !== (card.pack ?? '')) patch.pack = draftPack
      await onSave(patch)
      setMode('view')
      setDraft({})
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mobile-peek" role="dialog" aria-label={card.title ?? card.card_id}>
      <header className="mobile-peek__head">
        <button
          type="button"
          className="mobile-peek__close"
          onClick={() => {
            if (mode === 'edit' && dirty) {
              if (!window.confirm('Discard unsaved changes?')) return
            }
            onClose()
          }}
          aria-label="Close"
        >
          ×
        </button>
        <KindPill kind={draftKind} />
        <span className="mobile-peek__id mono">{card.card_id}</span>
        {mode === 'view' ? (
          <button
            type="button"
            className="mobile-peek__action"
            onClick={() => setMode('edit')}
          >
            Edit
          </button>
        ) : (
          <>
            <button
              type="button"
              className="mobile-peek__action mobile-peek__action--primary"
              onClick={commit}
              disabled={!dirty || saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="mobile-peek__action"
              onClick={() => {
                setMode('view')
                setDraft({})
                setErr(null)
              }}
            >
              Cancel
            </button>
          </>
        )}
      </header>

      {err && (
        <div className="mobile-toast mobile-toast--error" role="alert">
          {err}
        </div>
      )}

      <div className="mobile-peek__body">
        {mode === 'view' ? (
          <>
            {card.title && <h2 className="mobile-peek__title">{card.title}</h2>}
            <div className="mobile-peek__md md-chat">
              <CardBodyMarkdown
                body={card.body}
                knownIds={knownIds}
                onJumpToCard={onJumpToCard}
              />
            </div>
            {card.stale && (
              <div className="mobile-peek__stale-note mono">
                STALE — a decision this card depends on changed. Edit and Save to mark fresh.
              </div>
            )}
          </>
        ) : (
          <div className="mobile-peek__form">
            <label className="mobile-peek__label">
              <span className="mobile-peek__label-text">Kind</span>
              <select
                className="mobile-peek__select"
                value={draftKind}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, kind: e.target.value }))
                }
              >
                {KIND_SPECS.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label.toLowerCase()}
                  </option>
                ))}
              </select>
            </label>

            <label className="mobile-peek__label">
              <span className="mobile-peek__label-text">Title</span>
              <input
                type="text"
                className="mobile-peek__input"
                value={draftTitle}
                placeholder="(no title — first body line is used)"
                onChange={(e) =>
                  setDraft((d) => ({ ...d, title: e.target.value }))
                }
              />
            </label>

            <label className="mobile-peek__label">
              <span className="mobile-peek__label-text">Body</span>
              <textarea
                className="mobile-peek__textarea"
                value={draftBody}
                rows={10}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, body: e.target.value }))
                }
              />
            </label>

            <label className="mobile-peek__label">
              <span className="mobile-peek__label-text">Pack</span>
              <input
                type="text"
                className="mobile-peek__input"
                value={draftPack}
                placeholder="(blank)"
                onChange={(e) =>
                  setDraft((d) => ({ ...d, pack: e.target.value }))
                }
              />
            </label>
          </div>
        )}
      </div>
    </div>
  )
}
